import { noop } from 'lodash-es';

import { createStorageUtilsHooks } from './storageUtils';

type Options = Readonly<{
	/**
	 * if true, value will not be updated after it has been read once
	 *
	 * useful if the setter being called might trigger unwanted circular logic
	 */
	readOnce?: boolean;
}>;

type AsyncOptions = Options &
	Readonly<{
		/**
		 * if true, storage is neither read nor listened to, and the value stays `undefined` until it is false
		 *
		 * useful to hold off reading until the user's cookie preferences allow it
		 */
		skip?: boolean;
	}>;

type StorageHooks = Readonly<{
	useStorageValue: (key: string, options?: Options) => readonly [string | null, (val: string | null) => void];
	useStorageValueAsync: (
		key: string,
		options?: AsyncOptions,
	) => readonly [string | null | undefined, (val: string | null) => void];
}>;

export function createStorageHooks(storage: Storage | undefined): StorageHooks {
	const { useListener, useStorageCheck, useStorageValueAsyncBase, useStorageValueBase, useStorageValueSetter } =
		createStorageUtilsHooks(storage);

	return {
		useStorageValue: (key, { readOnce } = {}) => {
			useStorageCheck();
			const [value, setValue] = useStorageValueBase(key);
			const setStorageValue = useStorageValueSetter(key);
			useListener(key, readOnce ? noop : setValue);
			return [value, setStorageValue];
		},
		useStorageValueAsync: (key, { readOnce, skip = false } = {}) => {
			const [value, setValue] = useStorageValueAsyncBase();
			const setStorageValue = useStorageValueSetter(key);
			useListener(key, readOnce ? noop : setValue, { skip });
			return [value, setStorageValue];
		},
	};
}
